import { Info } from "lucide-react";
import { Panel } from "@/components/ui/Panel";
import { StepCard } from "@/components/patterns/StepCard";
import { useParams } from "react-router";
import { FileDropZone } from "@/components/shared/FileDropZone";
import {
  mergeFigures,
  useFigures,
  useUploadFigures,
} from "@/hooks/use-figures";
import { useDeleteFile } from "@/hooks/use-files";
import { useFigureDefinitions } from "@/hooks/use-figure-definitions";
import type { SetAsideFigureResponse } from "@patent-studio/api-types";
import type { SetAsideActsProps } from "@/v2/figures/DeletedFigures";
import { DeletedFigureCards } from "./DeletedFigureCards";
import { useTask } from "@/hooks/use-tasks";
import { FIGURE_ACCEPT, FIGURE_ACCEPT_LABEL } from "@/lib/constants";
import { FigureOrganiser } from "./FigureOrganiser";
import { PreparingDrawings } from "./PreparingDrawings";
import { StepLink } from "./StepLink";
import { StagePage } from "../layout/StagePage";
import { stageHeadingLabel } from "@/v2/stage-vocabulary";
import { StageSources } from "@/v2/layout/StageSources";

/**
 * The way on to Define. Ready off the served figures, not the merged list, because a
 * placeholder is an upload the server has not confirmed and Define would still call
 * the set empty.
 */
function WayOn({
  taskId,
  ready,
}: {
  taskId: string | undefined;
  ready: boolean;
}) {
  return (
    <div className="mt-4 flex justify-end">
      <StepLink
        taskId={taskId}
        from="upload"
        direction="next"
        ready={ready}
        primary
      />
    </div>
  );
}

/**
 * Restore and Permanently Delete for the deleted figures listed here.
 *
 * Through the definitions queue, the one Define writes through, so neither races a write
 * still on its way.
 */
function useSetAsideActs(taskId: string | undefined): SetAsideActsProps {
  const set = useFigureDefinitions(taskId);
  return {
    onRestore: (figure: SetAsideFigureResponse) => {
      void set.restoreFigure(figure.file_id);
    },
    onPurge: (figure: SetAsideFigureResponse) => {
      void set.purgeFigure(figure.file_id);
    },
  };
}

/**
 * Upload and organise the application's figures.
 *
 * Two steps on one screen, because the arrangement is what produces the labels
 * and an attorney who uploads without ordering has not finished the job.
 */
export function FiguresStagePane() {
  const { taskId } = useParams<{ taskId: string }>();
  const { data } = useFigures(taskId);
  const { data: task } = useTask(taskId ?? null);
  const upload = useUploadFigures(taskId);
  // Only a supplied PDF is removed here. The drawings themselves come off
  // through the organiser, which has the layout to repair as well as the file.
  const removeSource = useDeleteFile(taskId ?? "");
  // Pending rows live outside the cache, so they survive another upload's
  // refetch. The merge is shared with the intake slot, which draws the same two
  // lists the same way.
  const figures = mergeFigures(data?.figures, upload.pending);
  const setAside = useSetAsideActs(taskId);

  return (
    <StagePage
      guide="figures-upload"
      testId="figures-stage-pane"
      width="wide"
      taskId={taskId}
      eyebrow={`${stageHeadingLabel("figures")} · Upload & organise`}
      title="Add & organise your figures"
      lede="Upload your figures, then order and group them. Grouped figures take one number between them and are lettered apart."
    >
      <StageSources taskId={taskId} stage="figures" />
      <StepCard
        number={1}
        title="Upload"
        intro="Add every figure image you want in the application."
        testId="figure-step-upload"
      >
        <FileDropZone
          accept={FIGURE_ACCEPT}
          onDrop={upload.upload}
          title="Drag & drop figures, or browse"
          hint={
            <span data-testid="figure-accepted-formats">
              Figure images and PDFs of drawings — {FIGURE_ACCEPT_LABEL}
            </span>
          }
        />
        <PreparingDrawings
          taskId={taskId}
          files={task?.files}
          onRemove={removeSource.mutate}
        />
        <Panel
          as="p"
          shape="row"
          className="mt-3 flex items-start gap-2 bg-muted text-[11.5px] leading-relaxed text-muted-foreground"
        >
          <Info className="mt-0.5 size-4 flex-none" />
          <span>
            Upload <b className="font-bold">display-ready drawings only</b>. An
            image becomes a figure exactly as it is. In a PDF of drawings,{" "}
            <b className="font-bold">each page becomes its own figure</b>,
            border and title block included. Drawings that still need editing,
            and documents you would take a figure out of, are not accepted.
          </span>
        </Panel>
      </StepCard>

      <StepCard
        number={2}
        title="Organise"
        intro="Order your figures and group related views. Grouped figures re-letter, for example 4A and 4B."
        testId="figure-step-organise"
        beside={
          <span
            data-testid="figure-count"
            className="rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground"
          >
            {figures.length} {figures.length === 1 ? "figure" : "figures"}
          </span>
        }
      >
        <FigureOrganiser
          taskId={taskId}
          figures={figures}
          layout={data?.layout ?? { entries: [], version: 0 }}
        />
        <DeletedFigureCards deleted={data?.deleted ?? []} {...setAside} />
      </StepCard>

      <WayOn taskId={taskId} ready={(data?.figures ?? []).length > 0} />
    </StagePage>
  );
}
